import React, { useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import 'maplibre-gl/dist/maplibre-gl.css';

// Helper function to get marker icon based on facility subtype
const getMarkerIcon = (facilitySubtype) => {
  if (!facilitySubtype) return null;
  
  const subtype = facilitySubtype.toLowerCase();
  
  // Check if it's a camping-related facility
  if (subtype.includes('camp') || subtype.includes('site')) {
    // Return SVG data URL for tent icon (black and white)
    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="24" height="24">
        <!-- Tent body -->
        <polygon points="20,70 50,30 80,70" fill="#ffffff" stroke="#000000" stroke-width="2"/>
        <!-- Tent pole -->
        <line x1="50" y1="30" x2="50" y2="70" stroke="#000000" stroke-width="2"/>
        <!-- Door -->
        <polygon points="45,70 50,50 55,70" fill="#000000" opacity="0.4"/>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svgString)}`;
  }
  
  return null; // For now, only return icon for camping
};

// Add custom styles to ensure navigation controls are interactive
const mapStyles = `
  .maplibregl-ctrl-group {
    pointer-events: auto !important;
    z-index: 1001 !important;
  }
  .maplibregl-ctrl-zoom-in,
  .maplibregl-ctrl-zoom-out {
    pointer-events: auto !important;
    cursor: pointer !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('maplibre-nav-fix');
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = 'maplibre-nav-fix';
    style.textContent = mapStyles;
    document.head.appendChild(style);
  }
}

// Helper function to safely create popup HTML content (XSS protection)
const createPopupContent = (name, latitude, longitude, color = '#3b82f6', website = null) => {
  const div = document.createElement('div');
  div.style.minWidth = '180px';
  div.style.maxWidth = '240px';
  
  // Create sanitized content using textContent instead of innerHTML
  const nameSpan = document.createElement('div');
  nameSpan.style.fontWeight = '600';
  nameSpan.style.color = color;
  nameSpan.style.marginBottom = '4px';
  nameSpan.textContent = name;
  
  const latDiv = document.createElement('div');
  latDiv.style.fontSize = '13px';
  latDiv.style.color = '#334155';
  latDiv.textContent = `Lat: ${latitude}`;
  
  const lngDiv = document.createElement('div');
  lngDiv.style.fontSize = '13px';
  lngDiv.style.color = '#334155';
  lngDiv.textContent = `Lng: ${longitude}`;
  
  const link = document.createElement('a');
  link.href = `https://www.google.com/maps?q=${latitude},${longitude}`;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'inline-block';
  link.style.marginTop = '8px';
  link.style.fontSize = '13px';
  link.style.color = '#2563eb';
  link.style.textDecoration = 'underline';
  link.textContent = 'View on Google Maps';
  
  div.appendChild(nameSpan);
  div.appendChild(latDiv);
  div.appendChild(lngDiv);
  div.appendChild(link);
  
  // Add website link if provided
  if (website && website.trim()) {
    const websiteLink = document.createElement('a');
    let href = website;
    if (!href.startsWith('http://') && !href.startsWith('https://')) {
      href = 'https://' + href;
    }
    websiteLink.href = href;
    websiteLink.target = '_blank';
    websiteLink.rel = 'noopener noreferrer';
    websiteLink.style.display = 'block';
    websiteLink.style.marginTop = '12px';
    websiteLink.style.fontSize = '13px';
    websiteLink.style.color = '#2563eb';
    websiteLink.style.textDecoration = 'underline';
    websiteLink.textContent = 'Visit Website';
    div.appendChild(websiteLink);
  }
  
  return div;
};

const BLMMapContainer = ({
  basemap = 'osm',
  showStates = true,
  locatedBlm,
  vendorMarkersInView = [],
  blmFacilitiesInView = [],
  recAreaMarkersInView = [],
  providersInView = [],
  onLocationSelect,
  onMarkerClick,
  onMapBoundsChange,
  showBlmPoints = false,
  showBlmNames = false,
  showVendors = false,
  showProviders = false
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerPopupRef = useRef(null);

  // Initialize map
  useEffect(() => {
    let isUnmounted = false;
    let mapInstance;
    const statesLayerIds = ['states-fill', 'states-line'];

    const initializeMap = async () => {
      const maplibregl = await import('maplibre-gl');
      if (!mapContainerRef.current || isUnmounted) return;

      // Remove previous map instance if exists
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      let style;

      // Choose basemap style
      if (basemap === 'usgs') {
        style = {
          version: 8,
          sources: {
            'usgs-tiles': {
              type: 'raster',
              tiles: [
                'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}'
              ],
              tileSize: 256,
              attribution: 'USGS National Map'
            }
          },
          layers: [
            {
              id: 'usgs-tiles',
              type: 'raster',
              source: 'usgs-tiles',
              minzoom: 0,
              maxzoom: 20
            }
          ]
        };
      } else if (basemap === 'relief') {
        style = {
          version: 8,
          sources: {
            'relief-tiles': {
              type: 'raster',
              tiles: [
                'https://basemap.nationalmap.gov/arcgis/rest/services/USGSShadedReliefOnly/MapServer/tile/{z}/{y}/{x}'
              ],
              tileSize: 256,
              attribution: 'USGS Shaded Relief'
            }
          },
          layers: [
            {
              id: 'relief-tiles',
              type: 'raster',
              source: 'relief-tiles',
              minzoom: 0,
              maxzoom: 20
            }
          ]
        };
      } else if (basemap === 'osm') {
        style = {
          version: 8,
          sources: {
            'osm-tiles': {
              type: 'raster',
              tiles: [
                'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
              ],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors'
            }
          },
          layers: [
            {
              id: 'osm-tiles',
              type: 'raster',
              source: 'osm-tiles',
              minzoom: 0,
              maxzoom: 19
            }
          ]
        };
      } else {
        style = 'https://demotiles.maplibre.org/globe.json';
      }

      mapInstance = new maplibregl.Map({
        container: mapContainerRef.current,
        style,
        center: [-104.9903, 39.7392],
        zoom: 5
      });

      mapInstanceRef.current = mapInstance;

      // Handle map load
      mapInstance.on('load', async () => {
        if (isUnmounted) return;
        
        // Add navigation controls AFTER map is loaded to ensure they work properly
        mapInstance.addControl(new maplibregl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: false
        }), 'top-right');
        
        // Handle states layer visibility
        for (const layerId of statesLayerIds) {
          if (mapInstance.getLayer(layerId)) {
            mapInstance.setLayoutProperty(layerId, 'visibility', showStates ? 'visible' : 'none');
          }
        }

        // Clean up existing located marker
        if (mapInstance.getLayer('located-blm-marker')) {
          mapInstance.removeLayer('located-blm-marker');
        }
        if (mapInstance.getSource('located-blm-marker')) {
          mapInstance.removeSource('located-blm-marker');
        }

        // Trigger initial bounds change to set map bounds
        if (onMapBoundsChange) {
          const bounds = mapInstance.getBounds();
          onMapBoundsChange(bounds);
        }

        // Add click handler to close popup when clicking on map background
        const handleMapClick = (e) => {
          // Only close popup if not clicking a marker layer
          const markerLayers = [
            'located-blm-marker',
            'rec-area-markers',
            'blm-facilities-markers',
            'vendor-markers'
          ];
          // If no features or features are not from marker layers, close popup
          const features = mapInstance.queryRenderedFeatures(e.point);
          const isMarker = features.some(f => markerLayers.includes(f.layer.id));
          if (!isMarker && markerPopupRef.current) {
            markerPopupRef.current.remove();
            markerPopupRef.current = null;
          }
        };
        mapInstance.on('click', handleMapClick);
      });

      // Handle map bounds change
      mapInstance.on('moveend', () => {
        if (isUnmounted || !onMapBoundsChange) return;
        const bounds = mapInstance.getBounds();
        onMapBoundsChange(bounds);
      });
    };

    initializeMap();

    return () => {
      isUnmounted = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [basemap, showStates]);

  // Handle located BLM marker
  useEffect(() => {
    if (!mapInstanceRef.current || !locatedBlm) return;

    const mapInstance = mapInstanceRef.current;
    
    // Remove existing marker
    if (mapInstance.getLayer('located-blm-marker')) {
      mapInstance.removeLayer('located-blm-marker');
    }
    if (mapInstance.getSource('located-blm-marker')) {
      mapInstance.removeSource('located-blm-marker');
    }

    if (locatedBlm) {
      // Handle both BLM facilities and rec areas data structures
      const latitude = locatedBlm.latitude || locatedBlm.RecAreaLatitude;
      const longitude = locatedBlm.longitude || locatedBlm.RecAreaLongitude;
      const name = locatedBlm.name || locatedBlm.RecAreaName;
      
      if (latitude !== null && longitude !== null && latitude !== undefined && longitude !== undefined) {
        mapInstance.addSource('located-blm-marker', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [longitude, latitude] },
                properties: { 
                  name: name, 
                  latitude: latitude, 
                  longitude: longitude 
                }
              }
            ]
          }
        });

        // Use circle layer for located marker to match green marker behavior
        mapInstance.addLayer({
          id: 'located-blm-marker',
          type: 'circle',
          source: 'located-blm-marker',
          paint: {
            'circle-radius': 6,
            'circle-color': '#3b82f6',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
          }
        });

        // Fly to the marker location
        mapInstance.flyTo({ center: [longitude, latitude], zoom: 12 });

        // Add click event for marker
        mapInstance.on('click', 'located-blm-marker', (e) => {
          const feature = e.features && e.features[0];
          if (feature && onLocationSelect) {
            onLocationSelect({
              name: feature.properties.name,
              lat: feature.properties.latitude,
              lon: feature.properties.longitude
            });

            // Close previous popup if open
            if (markerPopupRef.current) {
              markerPopupRef.current.remove();
            }

            // Create popup content with name, coordinates, and Google Maps link
            const popupContent = createPopupContent(feature.properties.name, feature.properties.latitude, feature.properties.longitude, '#3b82f6');

            // Create and show popup
            const popup = new (require('maplibre-gl')).Popup({
              closeButton: true,
              closeOnClick: false,
              offset: 15
            })
              .setLngLat([feature.properties.longitude, feature.properties.latitude])
              .setDOMContent(popupContent)
              .addTo(mapInstance);

            markerPopupRef.current = popup;
          }
        });

        // Change cursor on hover
        mapInstance.on('mouseenter', 'located-blm-marker', () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'located-blm-marker', () => {
          mapInstance.getCanvas().style.cursor = '';
        });
      }
    }
  }, [locatedBlm, onLocationSelect]);

  // Handle vendor markers
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const mapInstance = mapInstanceRef.current;

    // Remove existing vendor markers
    if (mapInstance.getLayer('vendor-markers')) {
      mapInstance.removeLayer('vendor-markers');
    }
    if (mapInstance.getSource('vendor-markers')) {
      mapInstance.removeSource('vendor-markers');
    }

    // Add SVG image for vendor marker if not already present
    const vendorMarkerId = 'vendor-marker-svg';

    let isAddingImage = false;
    
    function addSVGImage() {
      const svgTeardrop = `
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="85" viewBox="0 0 96 128" aria-label="Outdoorec compass marker">
          <path d="M48 4
                   C28 4 12 20 12 40
                   C12 68 48 124 48 124
                   C48 124 84 68 84 40
                   C84 20 68 4 48 4Z"
                fill="none" stroke="#9333ea" stroke-width="6"/>
          <circle cx="48" cy="44" r="20" fill="white" stroke="#9333ea" stroke-width="4"/>
          <g transform="translate(48,44)" fill="#9333ea">
            <line x1="0" y1="0" x2="-12" y2="-12" stroke="#9333ea" stroke-width="3"/>
            <polygon points="-15,-15 -9,-9 -12,-12"/>
            <line x1="0" y1="0" x2="12" y2="12" stroke="#9333ea" stroke-width="3"/>
            <polygon points="15,15 9,9 12,12"/>
            <circle cx="0" cy="0" r="3" fill="#9333ea"/>
          </g>
        </svg>
      `;
      
      // Check if image exists or is currently being added
      if (!mapInstance.hasImage(vendorMarkerId) && !isAddingImage) {
        isAddingImage = true;
        const img = new window.Image();
        const svg = new Blob([svgTeardrop], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(svg);
        img.onload = () => {
          try {
            // Double-check before adding in case multiple calls happened
            if (!mapInstance.hasImage(vendorMarkerId)) {
              mapInstance.addImage(vendorMarkerId, img, { pixelRatio: 2 });
            }
          } catch (error) {
            console.warn('Failed to add vendor marker image:', error.message);
          } finally {
            isAddingImage = false;
            URL.revokeObjectURL(url);
          }
        };
        img.onerror = () => {
          isAddingImage = false;
          URL.revokeObjectURL(url);
        };
        img.src = url;
      }
    }

    // Add SVG image initially and on style reloads
    addSVGImage();
    // Remove previous styledata listener if present
    mapInstance.off && mapInstance.off('styledata', addSVGImage);
    mapInstance.on('styledata', addSVGImage);

    if (vendorMarkersInView.length > 0 && showVendors) {
      const features = vendorMarkersInView.map(item => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [item.longitude, item.latitude] },
        properties: {
          vendor_name: item.vendor_name,
          latitude: item.latitude,
          longitude: item.longitude,
          rating: item.rating,
          website: item.website,
          formatted_address: item.formatted_address,
          international_phone_number: item.international_phone_number,
          category: item.category,
          state: item.state,
          content: item.content
        }
      }));

      mapInstance.addSource('vendor-markers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features
        }
      });

      // Add vendor markers as symbol layer with SVG
      mapInstance.addLayer({
        id: 'vendor-markers',
        type: 'symbol',
        source: 'vendor-markers',
        layout: {
          'icon-image': vendorMarkerId,
          'icon-size': 1.2,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true
        }
      });

      // Add click handlers for vendor markers
      mapInstance.on('click', 'vendor-markers', (e) => {
        const feature = e.features && e.features[0];
        if (feature) {
          if (onMarkerClick) {
            onMarkerClick({
              vendor_name: feature.properties.vendor_name,
              name: feature.properties.vendor_name,
              latitude: feature.properties.latitude,
              longitude: feature.properties.longitude,
              rating: feature.properties.rating,
              website: feature.properties.website,
              formatted_address: feature.properties.formatted_address,
              international_phone_number: feature.properties.international_phone_number,
              category: feature.properties.category,
              state: feature.properties.state,
              content: feature.properties.content
            });
          }
          if (onLocationSelect) {
            onLocationSelect({
              name: feature.properties.vendor_name,
              lat: feature.properties.latitude,
              lon: feature.properties.longitude
            });
          }
          if (markerPopupRef.current) {
            markerPopupRef.current.remove();
          }
          const popupContent = createPopupContent(feature.properties.vendor_name, feature.properties.latitude, feature.properties.longitude, '#9333ea', feature.properties.website);
          const popup = new (require('maplibre-gl')).Popup({
            closeButton: true,
            closeOnClick: false,
            offset: 15
          })
            .setLngLat([feature.properties.longitude, feature.properties.latitude])
            .setDOMContent(popupContent)
            .addTo(mapInstance);
          markerPopupRef.current = popup;
        }
      });

      mapInstance.on('mouseenter', 'vendor-markers', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });

      mapInstance.on('mouseleave', 'vendor-markers', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
    }
    
    // Cleanup event handlers on unmount or dependency change
    return () => {
      if (mapInstance && mapInstance.off) {
        mapInstance.off('click', 'vendor-markers');
        mapInstance.off('mouseenter', 'vendor-markers');
        mapInstance.off('mouseleave', 'vendor-markers');
      }
    };
  }, [vendorMarkersInView, showVendors, onLocationSelect]);

  // Handle outdoor recreation providers markers
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const mapInstance = mapInstanceRef.current;

    // Remove existing provider markers
    if (mapInstance.getLayer('provider-markers')) {
      mapInstance.removeLayer('provider-markers');
    }
    if (mapInstance.getSource('provider-markers')) {
      mapInstance.removeSource('provider-markers');
    }

    // Add SVG image for provider marker if not already present
    const providerMarkerId = 'provider-marker-svg';

    let isAddingImage = false;

    function addSVGImage() {
      const svgTeardrop = `
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="85" viewBox="0 0 96 128" aria-label="Outdoor recreation provider marker">
          <path d="M48 4
                   C28 4 12 20 12 40
                   C12 68 48 124 48 124
                   C48 124 84 68 84 40
                   C84 20 68 4 48 4Z"
                fill="none" stroke="#f97316" stroke-width="6"/>
          <circle cx="48" cy="44" r="20" fill="white" stroke="#f97316" stroke-width="4"/>
          <g transform="translate(48,44)" fill="#f97316">
            <text x="0" y="8" text-anchor="middle" font-size="20" font-weight="bold">🏕</text>
          </g>
        </svg>
      `;

      if (!mapInstance.hasImage(providerMarkerId) && !isAddingImage) {
        isAddingImage = true;
        const img = new window.Image();
        const svg = new Blob([svgTeardrop], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(svg);
        img.onload = () => {
          try {
            if (!mapInstance.hasImage(providerMarkerId)) {
              mapInstance.addImage(providerMarkerId, img, { pixelRatio: 2 });
            }
          } catch (error) {
            console.warn('Failed to add provider marker image:', error.message);
          } finally {
            isAddingImage = false;
            URL.revokeObjectURL(url);
          }
        };
        img.onerror = () => {
          isAddingImage = false;
          URL.revokeObjectURL(url);
        };
        img.src = url;
      }
    }

    // Add SVG image initially and on style reloads
    addSVGImage();
    mapInstance.off && mapInstance.off('styledata', addSVGImage);
    mapInstance.on('styledata', addSVGImage);

    if (providersInView.length > 0 && showProviders) {
      const features = providersInView.map(item => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [item.longitude, item.latitude] },
        properties: {
          company_name: item["Company Name"] || item.company_name || "Unknown Provider",
          latitude: item.latitude,
          longitude: item.longitude,
          location: item["Location"] || item.location || "",
          url: item["URL"] || item.url || "",
          approved: item["Outdoorec Approved"] || item.outdoorec_approved || ""
        }
      }));

      mapInstance.addSource('provider-markers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features
        }
      });

      // Add provider markers as symbol layer with SVG
      mapInstance.addLayer({
        id: 'provider-markers',
        type: 'symbol',
        source: 'provider-markers',
        layout: {
          'icon-image': providerMarkerId,
          'icon-size': 0.8,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true
        }
      });

      // Add click handlers for provider markers
      mapInstance.on('click', 'provider-markers', (e) => {
        const feature = e.features && e.features[0];
        if (feature) {
          if (onMarkerClick) {
            onMarkerClick({
              company_name: feature.properties.company_name,
              name: feature.properties.company_name,
              latitude: feature.properties.latitude,
              longitude: feature.properties.longitude,
              location: feature.properties.location,
              url: feature.properties.url,
              approved: feature.properties.approved
            });
          }
          if (onLocationSelect) {
            onLocationSelect({
              name: feature.properties.company_name,
              lat: feature.properties.latitude,
              lon: feature.properties.longitude
            });
          }
          if (markerPopupRef.current) {
            markerPopupRef.current.remove();
          }
          const popupContent = createPopupContent(feature.properties.company_name, feature.properties.latitude, feature.properties.longitude, '#f97316', feature.properties.url);
          const popup = new (require('maplibre-gl')).Popup({
            closeButton: true,
            closeOnClick: false,
            offset: 15
          })
            .setLngLat([feature.properties.longitude, feature.properties.latitude])
            .setDOMContent(popupContent)
            .addTo(mapInstance);
          markerPopupRef.current = popup;
        }
      });

      mapInstance.on('mouseenter', 'provider-markers', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });

      mapInstance.on('mouseleave', 'provider-markers', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
    }

    // Cleanup event handlers on unmount or dependency change
    return () => {
      if (mapInstance && mapInstance.off) {
        mapInstance.off('click', 'provider-markers');
        mapInstance.off('mouseenter', 'provider-markers');
        mapInstance.off('mouseleave', 'provider-markers');
      }
    };
  }, [providersInView, showProviders, onLocationSelect]);

  // Handle recreation area markers
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const mapInstance = mapInstanceRef.current;

    // Remove existing rec area markers
    if (mapInstance.getLayer('rec-area-markers')) {
      mapInstance.removeLayer('rec-area-markers');
    }
    if (mapInstance.getSource('rec-area-markers')) {
      mapInstance.removeSource('rec-area-markers');
    }

    if (recAreaMarkersInView.length > 0 && showBlmNames) {
      const features = recAreaMarkersInView.map(item => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [item.RecAreaLongitude, item.RecAreaLatitude] },
        properties: {
          id: item.id || item.RecAreaID || `${item.RecAreaLatitude}_${item.RecAreaLongitude}`,
          name: item.RecAreaName || item.name,
          latitude: item.RecAreaLatitude || item.latitude,
          longitude: item.RecAreaLongitude || item.longitude,
          description: item.RecAreaDescription || item.description || 'No description available',
          state: item.RecAreaState || item.state
        }
      }));

      mapInstance.addSource('rec-area-markers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features
        }
      });

      mapInstance.addLayer({
        id: 'rec-area-markers',
        type: 'circle',
        source: 'rec-area-markers',
        paint: {
          'circle-radius': 6,
          'circle-color': '#059669',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        }
      });

      // Add click handlers for rec area markers
      mapInstance.on('click', 'rec-area-markers', (e) => {
        const feature = e.features && e.features[0];
        if (feature) {
          // Set current selection in sidebar via new marker click handler
          if (onMarkerClick) {
            onMarkerClick({
              id: feature.properties.id,
              name: feature.properties.name,
              RecAreaName: feature.properties.name,
              RecAreaLatitude: feature.properties.latitude,
              RecAreaLongitude: feature.properties.longitude,
              RecAreaDescription: feature.properties.description,
              RecAreaState: feature.properties.state,
              latitude: feature.properties.latitude,
              longitude: feature.properties.longitude,
              description: feature.properties.description,
              state: feature.properties.state
            });
          }
          
          // Also call legacy onLocationSelect for backward compatibility
          if (onLocationSelect) {
            onLocationSelect({
              name: feature.properties.name,
              lat: feature.properties.latitude,
              lon: feature.properties.longitude
            });
          }

          // Close previous popup if open
          if (markerPopupRef.current) {
            markerPopupRef.current.remove();
          }
          // Create popup content
          const popupContent = createPopupContent(feature.properties.name, feature.properties.latitude, feature.properties.longitude, '#059669');
          const popup = new (require('maplibre-gl')).Popup({
            closeButton: true,
            closeOnClick: false,
            offset: 15
          })
            .setLngLat([feature.properties.longitude, feature.properties.latitude])
            .setDOMContent(popupContent)
            .addTo(mapInstance);
          markerPopupRef.current = popup;
        }
      });

      mapInstance.on('mouseenter', 'rec-area-markers', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });

      mapInstance.on('mouseleave', 'rec-area-markers', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
    }
    
    // Cleanup event handlers on unmount or dependency change
    return () => {
      if (mapInstance && mapInstance.off) {
        mapInstance.off('click', 'rec-area-markers');
        mapInstance.off('mouseenter', 'rec-area-markers');
        mapInstance.off('mouseleave', 'rec-area-markers');
      }
    };
  }, [recAreaMarkersInView, showBlmNames, onLocationSelect]);

  // Handle BLM facilities markers (different from rec areas)
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const mapInstance = mapInstanceRef.current;

    // Show BLM facilities markers when showBlmPoints is true
    if (blmFacilitiesInView.length > 0 && showBlmPoints) {
      const features = blmFacilitiesInView
        .filter(item => item.longitude && item.latitude && 
                       !isNaN(item.longitude) && !isNaN(item.latitude))
        .map(item => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [item.longitude, item.latitude] },
          properties: {
            id: item.id || `${item.latitude}_${item.longitude}`,
            name: item.name || 'Unnamed Facility',
            latitude: item.latitude,
            longitude: item.longitude,
            description: item.description || item.facility_subtype || 'No description available',
            state: item.state,
            facility_subtype: item.facility_subtype
          }
        }));

      if (features.length > 0) {
        const sourceExists = !!mapInstance.getSource('blm-facilities-markers');
        
        if (sourceExists) {
          // Just update the data if source exists
          mapInstance.getSource('blm-facilities-markers').setData({
            type: 'FeatureCollection',
            features
          });
        } else {
          // First time: create source and layer
          mapInstance.addSource('blm-facilities-markers', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features
            }
          });

          // Check if any features have camping subtypes to determine layer type
          const hasCampingFacilities = features.some(f => 
            f.properties.facility_subtype && 
            f.properties.facility_subtype.toLowerCase().includes('camp')
          );

          // Always use circle layer for BLM facilities to match green marker behavior
          mapInstance.addLayer({
            id: 'blm-facilities-markers',
            type: 'circle',
            source: 'blm-facilities-markers',
            paint: {
              'circle-radius': 6,
              'circle-color': '#3b82f6', // Blue color for BLM facilities
              'circle-stroke-width': 2,
              'circle-stroke-color': '#fff'
            }
          });

          // Add click handlers for BLM facilities markers
          mapInstance.on('click', 'blm-facilities-markers', (e) => {
            const feature = e.features && e.features[0];
            if (feature) {
              // Set current selection in sidebar via new marker click handler
              if (onMarkerClick) {
                onMarkerClick({
                  id: feature.properties.id,
                  name: feature.properties.name,
                  latitude: feature.properties.latitude,
                  longitude: feature.properties.longitude,
                  description: feature.properties.description,
                  state: feature.properties.state,
                  facility_subtype: feature.properties.facility_subtype
                });
              }
              
              // Also call legacy onLocationSelect for backward compatibility
              if (onLocationSelect) {
                onLocationSelect({
                  name: feature.properties.name,
                  lat: feature.properties.latitude,
                  lon: feature.properties.longitude
                });
              }

              // Close previous popup if open
              if (markerPopupRef.current) {
                markerPopupRef.current.remove();
              }
              // Create popup content
              const popupContent = createPopupContent(feature.properties.name, feature.properties.latitude, feature.properties.longitude, '#3b82f6');
              const popup = new (require('maplibre-gl')).Popup({
                closeButton: true,
                closeOnClick: false,
                offset: 15
              })
                .setLngLat([feature.properties.longitude, feature.properties.latitude])
                .setDOMContent(popupContent)
                .addTo(mapInstance);
              markerPopupRef.current = popup;
            }
          });

          mapInstance.on('mouseenter', 'blm-facilities-markers', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
          });

          mapInstance.on('mouseleave', 'blm-facilities-markers', () => {
            mapInstance.getCanvas().style.cursor = '';
          });
        }
      }
    } else {
      // Remove existing BLM facilities markers when showBlmPoints is false
      if (mapInstance.getLayer('blm-facilities-markers')) {
        mapInstance.removeLayer('blm-facilities-markers');
      }
      if (mapInstance.getSource('blm-facilities-markers')) {
        mapInstance.removeSource('blm-facilities-markers');
      }
    }
    
    // Cleanup event handlers on unmount or dependency change
    return () => {
      if (mapInstance && mapInstance.off) {
        mapInstance.off('click', 'blm-facilities-markers');
        mapInstance.off('mouseenter', 'blm-facilities-markers');
        mapInstance.off('mouseleave', 'blm-facilities-markers');
      }
    };
  }, [blmFacilitiesInView, showBlmPoints, onLocationSelect]);

  return (
    <div style={{ flex: 1, position: 'relative' }}>
      <div 
        ref={mapContainerRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          pointerEvents: 'auto' // Ensure map container can receive pointer events
        }}
      ></div>
    </div>
  );
};

export default BLMMapContainer;